import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@parrot-software/design-system';

import type { PlacePredictionDto } from '@onboarding/application/dtos';

type SuggestionsDropdownProps = {
  /** Ref to the element the dropdown aligns below */
  anchorRef: React.RefObject<HTMLDivElement>;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onSelect: (placeId: string) => void;
  predictions: PlacePredictionDto[];
};

/**
 * Dropdown list of address autocomplete suggestions.
 * Rendered via portal into the closest [role="dialog"] so it escapes overflow
 * clipping while staying inside the Dialog's stacking context for proper event handling.
 * Uses position:fixed with viewport coordinates from getBoundingClientRect.
 * onMouseDown with preventDefault keeps focus on the textarea when clicking suggestions.
 */
export const SuggestionsDropdown = ({
  anchorRef,
  activeIndex,
  onActiveIndexChange,
  onSelect,
  predictions,
}: SuggestionsDropdownProps) => {
  const listRef = useRef<HTMLUListElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (anchorRef.current) {
      const dialog = anchorRef.current.closest('[role="dialog"]') as HTMLElement | null;
      setPortalContainer(dialog);
    }
  }, [anchorRef]);

  const updatePosition = useCallback(() => {
    if (!anchorRef.current || !portalContainer) return;
    const anchorRect = anchorRef.current.getBoundingClientRect();
    const containerRect = portalContainer.getBoundingClientRect();
    setPosition({
      top: anchorRect.bottom - containerRect.top + 4,
      left: anchorRect.left - containerRect.left,
      width: anchorRect.width,
    });
  }, [anchorRef, portalContainer]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [updatePosition]);

  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const activeItem = listRef.current.children[activeIndex] as HTMLElement | undefined;
      activeItem?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  if (!position || !portalContainer) return null;

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <ul
      className="absolute z-9999 max-h-60 overflow-auto rounded-md border border-border bg-background shadow-lg"
      onMouseDown={(e) => e.preventDefault()}
      ref={listRef}
      role="listbox"
      style={{ top: position.top, left: position.left, width: position.width }}
    >
      {predictions.map((prediction, index) => (
        <li
          aria-selected={index === activeIndex}
          className={cn('cursor-pointer px-3 py-2 text-sm', index === activeIndex ? 'bg-accent' : '')}
          key={prediction.placeId}
          onClick={() => onSelect(prediction.placeId)}
          onMouseEnter={() => onActiveIndexChange(index)}
          onMouseLeave={() => onActiveIndexChange(-1)}
          role="option"
        >
          <span className="font-medium">{prediction.mainText}</span>
          <span className="ml-1 text-muted-foreground">{prediction.secondaryText}</span>
        </li>
      ))}
    </ul>,
    portalContainer,
  );
};
